import { useState } from 'react';
import PhotoThumb from '../../PhotoThumb';
import { resolvePhotoUrl } from '../../../utils/photoUrl';
import { startIsolateBatch } from '../../../utils/spores/sporeBatch';
import { displayName } from '../../../utils/userLabel';
import { Day } from '../cells';
import * as S from '../../specimens/styles';

/*
 * Microscopy: the micrographs linked to the isolate -- its collection, intake
 * or strain, or one of its plates or slants (the dossier's `microscopy`) --
 * each with the latest spore analysis saved on it (`spore_analyses`: count,
 * mean length x width and Q, when and by whom) and a way into the analysis
 * workbench's microscopy mode (spore analysis) on that micrograph
 * (/tools/analysis?mode=microscopy&image=<id>), with the isolate's other
 * micrographs as its batch, so Next image goes on to them
 * (utils/spores/sporeBatch.js).
 */
export const sporeLink = (imageId) => `/tools/analysis?mode=microscopy&image=${encodeURIComponent(imageId)}`;

const num = (v, digits = 2) => (v === null || v === undefined ? '–' : Number(v).toFixed(digits));

/** One line for a saved analysis, in micrometres or, without a scale, its count only. */
export function analysisLine(a, t, key = 'summary') {
  if (!a) return null;
  if (a.mean_length_um === null || a.mean_length_um === undefined) {
    return t(key === 'summary' ? 'microscopy.summaryPx' : 'microscopy.bankLinePx',
             { count: a.count, when: (a.created_at || '').slice(0, 10) });
  }
  return t(key === 'summary' ? 'microscopy.summary' : 'microscopy.bankLine', {
    count: a.count, length: num(a.mean_length_um), width: num(a.mean_width_um), q: num(a.mean_q),
    when: (a.created_at || '').slice(0, 10),
  });
}

export default function MicroscopyTab({ item, dossier, t, onNavigate }) {
  const images = dossier.microscopy || [];
  const latest = Object.fromEntries((dossier.spore_analyses || []).map(a => [a.image_id, a]));
  const [broken, setBroken] = useState({});
  if (!images.length) return <p style={S.hint}>{t('microscopy.empty')}</p>;
  return (
    <ul className="hub-records" data-testid={`hub-microscopy-${item.specimen_id}`}>
      {images.map((m, i) => {
        const a = latest[m.image_id];
        const src = resolvePhotoUrl(m.thumbnail_url || m.url);
        return (
          <li key={m.image_id} className="hub-record" data-testid={`hub-micrograph-${m.image_id}`}
              style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
            {src && !broken[m.image_id] ? (
              <PhotoThumb photos={images.filter(x => x.url).map(x => ({ s3_url: x.url, photo_id: x.image_id }))}
                index={images.filter(x => x.url).findIndex(x => x.image_id === m.image_id)}
                testId={`hub-micrograph-open-${m.image_id}`} dialogLabel={t('microscopy.alt', { n: i + 1 })}
                disabled={!m.url}>
                <img src={src} alt={t('microscopy.alt', { n: i + 1 })} loading="lazy" width={120} height={80}
                     onError={() => setBroken(b => ({ ...b, [m.image_id]: true }))}
                     style={{ objectFit: 'cover', borderRadius: 6, background: '#000' }} />
              </PhotoThumb>
            ) : <div aria-hidden="true" style={placeholder}>🔬</div>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: 1 }}>
              <div style={S.hint}>
                {m.magnification ? <bdi>{t('microscopy.magnification', { value: m.magnification })}</bdi> : null}
                {m.magnification ? ' · ' : null}
                <bdi dir="ltr">{m.um_per_px ? t('microscopy.scale', { value: m.um_per_px }) : t('microscopy.noScale')}</bdi>
                {(m.captured_at || m.uploaded_at) && <> · <Day value={m.captured_at || m.uploaded_at} /></>}
                {m.content_type === 'image/tiff' && <> · {t('microscopy.tiff')}</>}
              </div>
              {m.link_kinds?.length > 0 && (
                <div style={S.hint}>
                  {t('microscopy.linked', { kinds: m.link_kinds.map(k => t(`microscopy.kind.${k}`)).join(', ') })}
                </div>
              )}
              <div data-testid={`hub-spore-summary-${m.image_id}`} style={{ fontSize: 13 }}>
                {a ? (
                  <>
                    <bdi dir="ltr">{analysisLine(a, t)}</bdi>
                    {a.created_by_name && (
                      <span style={S.hint}> · {t('microscopy.when', { when: (a.created_at || '').slice(0, 10),
                        by: displayName(a.created_by_name, t) })}</span>
                    )}
                  </>
                ) : <span style={S.hint}>{t('microscopy.noAnalysis')}</span>}
              </div>
              <div>
                <button type="button" style={S.ghostBtn} title={t('microscopy.openTip')}
                        data-testid={`hub-open-spores-${m.image_id}`}
                        onClick={() => { startIsolateBatch(item, dossier); onNavigate(sporeLink(m.image_id)); }}>
                  {t('microscopy.open')}
                </button>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

const placeholder = { width: 120, height: 80, borderRadius: 6, display: 'flex', alignItems: 'center',
                      justifyContent: 'center', background: 'var(--c-bg-muted)', fontSize: 24 };
